/* The silhouette fills the width it is given at a FIXED height. `figSvg` in KANON klienci v2 is
 * `width: 100%; height: 224px`; v3 draws it 258, which is what `--h-silhouette` carries.
 *
 * The frame is declared rather than taken from the viewBox, and that is what keeps a front and a
 * back the same height beside each other: the male pair is authored 724×1448 (exactly 1:2) but the
 * female pair is 734×1538 and 774×1448, so height-from-aspect would draw a woman's front figure
 * taller than her own back. The package solves it the same way — every figure is rendered into a
 * fixed box — and `preserveAspectRatio` then centres each drawing inside the frame.
 *
 * A fixed height rather than the 1:2 `aspect-ratio` this used to carry, for two reasons: it is what
 * v2 specifies, and a height that depends on the container's WIDTH is a height that is only correct
 * once the container has been laid out.
 *
 * This is the ONLY place the figure's box is written. The element used to repeat it as `width` /
 * `height` attributes and they drifted 34px behind this token — see the component. */
.figure {
  display: block;
  width: 100%;
  height: var(--h-silhouette);
}

/* Every shape on the figure is RINGED BY DEFAULT, and that is a contrast decision rather than a
 * style one.
 *
 * The artwork fills each muscle and draws no edge, so a region is only as visible as its fill's
 * contrast with the body beside it. Measured on the light theme against `--bg-chip`, which is what
 * an unstimulated muscle is painted: amber 2.44:1 (now 3.17, recorded on the token), green 2.80,
 * red 2.72 — all under the 3:1 WCAG asks of a meaningful graphic. And the ZERO zone is `--bg-chip`
 * on `--bg-inset`, 1.07:1, which is not a low-contrast shape but an invisible one: the figure had
 * no arms, in both themes.
 *
 * A ring fixes all four at once without repainting any, which is why the fills still mean exactly
 * what the caller's legend says they mean. `--border-3` is the app's "this edge IS the information"
 * step — 3.36:1 light, 3.63:1 dark.
 *
 * `non-scaling-stroke` because the viewBox is 724 units drawn into ~145px: one user unit is a
 * twentieth of a device pixel there, so a plain hairline would vanish entirely. This pins it at one
 * device pixel whatever the figure is scaled to.
 *
 * ── The ring is the CALLER's to turn off, and one caller does (owner, 16 September 2026) ─────
 * `--silhouette-ring` defaults to the value above, so every surface that has not heard of it draws
 * exactly what it drew before. The reason the seam exists is that the argument above is about a
 * SCALE, not about the artwork: it holds wherever the fills are a categorical palette whose
 * contrast against each other cannot be chosen — the client detail's zones, the library rail's
 * levels. It does not hold where the fills ARE chosen with their separation in mind, which is what
 * the plan library's four `--silhouette-*` steps are. There the ring is not carrying information,
 * it is competing with it: a 0.38 white edge on every path is brighter than the `mid` step's fill,
 * so every muscle in the body was outlined at the strength of a worked one and the figure read as a
 * wireframe rather than as a body with parts of it lit. */
.part {
  stroke: var(--silhouette-ring, var(--border-3));
  stroke-width: var(--border-hairline);
  vector-effect: non-scaling-stroke;
}

/* A shape the caller made pressable (`interactive`). The pointer says so; the highlight is the
 * caller's fill, from the silhouette's own grey steps. */
.pressable {
  cursor: pointer;
}
